import * as Yup from 'yup';
import { useState, useEffect } from 'react';
import { useFormik } from 'formik';
import {
  Container,
  Grid,
  Card,
  CardHeader,
  CardContent,
  Divider,
  Button,
  Typography,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormHelperText,
  Autocomplete
} from '@mui/material';
import TextField from '@mui/material/TextField';
import { LoadingButton } from '@mui/lab';
import request from '../../utils/request';
import SnackbarAlert from '../common/SnackbarAlert';
import React from 'react';
import AutocompleteLoader from '../common/AutocompleteLoader';

function CreatEdit({ closeCreateEditForm, EndoresementSubTypeEditDatas, editStatus }) {
  const [policyProductData, setPolicyProductData] = useState([]);
  const [policyProductValue, setPolicyProductValue] = useState(null);
  const [policyProductSearchValue, setPolicyProductSearchValue] = useState('');
  const [productLoader, setProductLoader] = useState(false);
  const [noOptionsTextFlag, setNoOptionsTextFlag] = useState(false);

  const [loader, setLoader] = useState(false);
  const [showSnackBarAlert, setShowSnackBarAlert] = useState({
    status: false,
    type: 'success',
    message: null
  });

  const EndoresementSubTypeSchema = Yup.object().shape({
    productId:Yup.string().typeError('Product is required').required('Product is required'),
    type: Yup.string().required('Endorsement Type  is required'),
    name: Yup.string()
      .required('Endorsement Sub Type Name is required')
      .min(3, 'Endorsement Sub Type Name must be at least 3 characters')
      .trim('Endorsement Sub Type Name cannot include leading and trailing spaces'),
  });

  const formik = useFormik({
    initialValues: {
      id: null,
      type: '',
      name: '',
      status: true,
      productId: null
    },
    validationSchema: EndoresementSubTypeSchema,
    onSubmit: (values) => {
      setLoader(true);
      const URL = editStatus ? 'endorsement/updateEndoresementSubTypes' : 'endorsement/createEndoresementSubTypes';
      request({
        url: URL,
        method: 'POST',
        data: values
      })
        .then((response) => {
          if (response.status) {
            closeCreateEditForm(true);
            setLoader(false);
          } else {
            setLoader(false);
            setShowSnackBarAlert({
              status: true,
              type: 'error',
              message: response['message']
            });
          }
        })
        .catch(function (err) {
          setShowSnackBarAlert({
            status: true,
            type: 'error',
            message: err.message
          });
          setLoader(false);
        });
    }
  });

  // Set formik values in edit mode
  useEffect(() => {
    if (editStatus && EndoresementSubTypeEditDatas) {
      formik.setValues({
        id: EndoresementSubTypeEditDatas.id,
        name: EndoresementSubTypeEditDatas.name,
        type: EndoresementSubTypeEditDatas.type,
        productId: EndoresementSubTypeEditDatas.productId,
        status: true,
      });
    }
  }, [editStatus, EndoresementSubTypeEditDatas]);

  // Fetch product list
  useEffect(() => {
    getpolicyProductData();
  }, [policyProductSearchValue]);

  // Set Autocomplete value once product list is fetched
  useEffect(() => {
    if (
      editStatus &&
      EndoresementSubTypeEditDatas?.productId &&
      policyProductData.length > 0
    ) {
      const matchedProduct = policyProductData.find(
        (item) => item.id === EndoresementSubTypeEditDatas.productId
      );
      if (matchedProduct) {
        setPolicyProductValue(matchedProduct);
        setPolicyProductSearchValue(matchedProduct.name);
      }
    }
  }, [editStatus, policyProductData, EndoresementSubTypeEditDatas]);

  const getpolicyProductData = () => {
    setProductLoader(true);
    setPolicyProductData([]);
    request({
      url: 'policyProduct/getAllowedPolicyProducts',
      method: 'post'
    }).then(
      (response:any) => {
        setProductLoader(false);
        if (response?.status === false) {
          setNoOptionsTextFlag(true);
          setPolicyProductData([]);
          setShowSnackBarAlert({
            status: true,
            type: 'error',
            message: response.message
          });
        } else {
          setNoOptionsTextFlag(false);
          setPolicyProductData(response);
        }
      },
      (error) => {
        setProductLoader(false);
        console.log(error);
      }
    );
  };

  const capitalizeFirst = (str) => {
    return str ? str.charAt(0).toUpperCase() + str.slice(1) : '';
  };

  const handleSnackbarClose = () => {
    setShowSnackBarAlert({ status: false, type: null, message: null });
  };

  return (
    <>
      <SnackbarAlert
        showSnackBarAlert={showSnackBarAlert}
        handleSnackbarClose={handleSnackbarClose}
      />
      <Container maxWidth="lg">
        <Grid container direction="row" justifyContent="center" alignItems="stretch" spacing={3}>
          <Grid item xs={12} className="ListContainer">
            <Card>
              <CardHeader title={editStatus ? 'Update Endorsement Sub Type' : 'Create Endorsement Sub Type'} />
              <Divider />
              <CardContent>
                <form onSubmit={formik.handleSubmit}>
                  <Grid container spacing={2}>
                    <Grid item xs={6}>
                      <Autocomplete
                        id="policyProduct"
                        getOptionLabel={(option) => option?.name || ''}
                        options={policyProductData}
                        isOptionEqualToValue={(option, value) => option?.id === value?.id}
                        size="small"
                        value={policyProductValue}
                        inputValue={policyProductSearchValue}
                        loading={productLoader}
                        onChange={(event, newValue) => {
                          if (newValue) {
                            setPolicyProductValue(newValue);
                            setPolicyProductSearchValue(newValue.name);
                            formik.setFieldValue("productId", newValue.id);
                          } else {
                            setPolicyProductValue(null);
                            setPolicyProductSearchValue('');
                            formik.setFieldValue("productId", null);
                          }
                        }}
                        onInputChange={(event, newInputValue, reason) => {
                          if (reason === 'input') {
                            setPolicyProductSearchValue(newInputValue);
                            if (newInputValue === '') {
                              formik.setFieldValue("productId", null);
                              setPolicyProductValue(null);
                            }
                          }
                        }}
                        onBlur={formik.handleBlur}
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            label={
                              <>
                                Select Product{' '}
                                <Typography component="span" sx={{ color: 'red' }}>*</Typography>
                              </>
                            }
                            name="productId"
                            placeholder="Search Product"
                            error={formik.touched.productId && Boolean(formik.errors.productId)}
                            helperText={formik.touched.productId && formik.errors.productId}
                            InputProps={{
                              ...params.InputProps,
                              endAdornment: (
                                <>
                                  {params.InputProps.endAdornment}
                                  <AutocompleteLoader params={params} loader={productLoader} />
                                </>
                              )
                            }}
                          />
                        )}
                      />
                    </Grid>

                    <Grid item xs={6}>
                      <FormControl fullWidth variant="outlined" size="small">
                        <InputLabel
                          id="financial-select-label"
                          sx={{ color: formik.touched.type && formik.errors.type ? 'red' : 'inherit' }}
                          shrink={Boolean(formik.values.type)}
                        >
                          Endorsement Type <Typography component="span" sx={{ color: 'red' }}>*</Typography>
                        </InputLabel>
                        <Select
                          labelId="financial-select-label"
                          id="financial-select"
                          name="type"
                          value={formik.values.type}
                          onChange={formik.handleChange}
                          label="Endorsement Type"
                          error={formik.touched.type && Boolean(formik.errors.type)}
                        >
                          <MenuItem value="Financial">Financial</MenuItem>
                          <MenuItem value="Non-Financial">Non-Financial</MenuItem>
                        </Select>
                        {formik.touched.type && formik.errors.type && (
                          <FormHelperText sx={{ color: 'red' }}>{formik.errors.type}</FormHelperText>
                        )}
                      </FormControl>
                    </Grid>

                    <Grid item xs={6}>
                      <TextField
                        label={
                          <>
                            Endorsement Sub Type Name{' '}
                            <Typography component="span" sx={{ color: 'red' }}>*</Typography>
                          </>
                        }
                        fullWidth
                        size="small"
                        name="name"
                        value={capitalizeFirst(formik.values.name)}
                        onChange={formik.handleChange}
                        onBlur={formik.handleBlur}
                        error={formik.touched.name && Boolean(formik.errors.name)}
                        helperText={formik.touched.name && formik.errors.name}
                      />
                    </Grid>

                    <Grid container direction="row" justifyContent="center" alignItems="center" spacing={3} mt={2}>
                      <Grid item>
                        {loader ? (
                          <LoadingButton sx={{ mt: { xs: 2, md: 0 } }} loading variant="contained" type="submit">
                            Save
                          </LoadingButton>
                        ) : (
                          <Button sx={{ mt: { xs: 2, md: 0 } }} type="submit" variant="contained">
                            Save
                          </Button>
                        )}
                      </Grid>
                      <Grid item>
                        <Button
                          sx={{ mt: { xs: 2, md: 0 } }}
                          variant="contained"
                          color="error"
                          onClick={closeCreateEditForm}
                        >
                          Cancel
                        </Button>
                      </Grid>
                    </Grid>
                  </Grid>
                </form>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      </Container>
    </>
  );
}

export default CreatEdit;
